import { useNavigate } from 'react-router';

import {
  PartnerApplicationMachineContext,
  StoreDetailsFormValues,
} from '~domains/partner-application/entities/application';
import { getApplicationEventProperties } from '~domains/partner-application/shared/lib';

import { AddressSuggestionApiProvider } from '~platform/entities/address-suggestion';

import { ampli } from '~shared/analytics';
import { ROUTES } from '~shared/config/routes';

import { StoreAddress } from './store-address';
import { StoreDetails } from './store-details';
import { StoreMap } from './store-map';
import { StorePhotos } from './store-photos';
import { StoresList } from './stores-list';
import { StoresStart } from './stores-start';

import { useApplicationActions } from '../../partner-application';

export const StoresBlock = () => {
  const navigate = useNavigate();
  const state = PartnerApplicationMachineContext.useSelector(
    (snapshot) => snapshot
  );
  const storeDraft = PartnerApplicationMachineContext.useSelector(
    (snapshot) => snapshot.context.storeDraft
  );
  const stores = PartnerApplicationMachineContext.useSelector(
    (snapshot) => snapshot.context.pointsOfSales
  );
  const agents = PartnerApplicationMachineContext.useSelector(
    (snapshot) => snapshot.context.agents
  );
  const applicationId = PartnerApplicationMachineContext.useSelector(
    (snapshot) => snapshot.context.applicationId
  );
  const actor = PartnerApplicationMachineContext.useActorRef();
  const { updateApplication } = useApplicationActions();

  const onClose = () => navigate(ROUTES.partner.applicationMenu);

  const onBack = () => actor.send({ type: 'back' });

  const isOnlineDraftReady =
    state.matches({ storesBlock: { storeEdit: 'storeDraftReady' } }) &&
    storeDraft?.details?.type === 'ONLINE';
  const isOfflineDraftReady =
    state.matches({ storesBlock: { storeEdit: 'storeDraftReady' } }) &&
    storeDraft?.details?.type === 'OFFLINE';

  if (!state.matches('storesBlock')) return null;

  return (
    <AddressSuggestionApiProvider>
      {state.matches({ storesBlock: 'storesStart' }) && (
        <StoresStart
          onSubmit={() => {
            ampli.mpPaStoresStartScreenNextClick(
              getApplicationEventProperties(applicationId)
            );
            actor.send({ type: 'next' });
          }}
          onBack={() => {
            ampli.mpPaStoresStartScreenBackClick(
              getApplicationEventProperties(applicationId)
            );
            onBack();
          }}
          onClose={() => {
            ampli.mpPaStoresStartScreenCloseClick(
              getApplicationEventProperties(applicationId)
            );
            onClose();
          }}
        />
      )}
      {state.matches({ storesBlock: 'storesList' }) && (
        <StoresList
          applicationId={applicationId}
          stores={stores}
          agents={agents}
          onSubmit={() => {
            ampli.mpPaStoreListScreenNextClick(
              getApplicationEventProperties(applicationId)
            );
            actor.send({ type: 'next' });
          }}
          onBack={() => {
            ampli.mpPaStoreListScreenBackClick(
              getApplicationEventProperties(applicationId)
            );
            onBack();
          }}
          onEditStore={(storeId) => {
            ampli.mpPaStoreListScreenEditStoreClick(
              getApplicationEventProperties(applicationId)
            );
            actor.send({ type: 'store.edit', storeId });
          }}
          onAddStore={() => actor.send({ type: 'store.add' })}
          onClose={() => {
            ampli.mpPaStoreListScreenCloseClick(
              getApplicationEventProperties(applicationId)
            );
            onClose();
          }}
          onRemoveStore={async (storeId: string) => {
            ampli.mpPaStoreListScreenDeleteStoreClick(
              getApplicationEventProperties(applicationId)
            );
            actor.send({ type: 'store.remove', storeId });
            await updateApplication();
          }}
        />
      )}

      {(state.matches({ storesBlock: { storeEdit: 'storeDetails' } }) ||
        isOnlineDraftReady) && (
        <StoreDetails
          storeDraft={storeDraft}
          onSubmit={async (data) => {
            if (data.type === 'OFFLINE') {
              ampli.mpPaOfflineStoreInfoScreenNextClick(
                getApplicationEventProperties(applicationId)
              );
              actor.send({
                type: 'storeDetails.submitDataOffline',
                storeData: data,
              });
            } else if (data.type === 'ONLINE') {
              ampli.mpPaOnlineStoreInfoScreenNextClick(
                getApplicationEventProperties(applicationId)
              );
              actor.send({
                type: 'storeDetails.submitDataOnline',
                storeData: data,
              });
              await updateApplication({
                onError: () => {
                  actor.send({ type: 'storeDraftReady.invalidated' });
                },
              });
              actor.send({ type: 'next' });
            }
          }}
          onBack={(storeType: StoreDetailsFormValues['type']) => {
            if (storeType === 'OFFLINE') {
              ampli.mpPaOfflineStoreInfoScreenBackClick(
                getApplicationEventProperties(applicationId)
              );
            } else if (storeType === 'ONLINE') {
              ampli.mpPaOnlineStoreInfoScreenBackClick(
                getApplicationEventProperties(applicationId)
              );
            }

            onBack();
          }}
          onClose={(storeType: StoreDetailsFormValues['type']) => {
            if (storeType === 'OFFLINE') {
              ampli.mpPaOfflineStoreInfoScreenCloseClick(
                getApplicationEventProperties(applicationId)
              );
            } else if (storeType === 'ONLINE') {
              ampli.mpPaOnlineStoreInfoScreenCloseClick(
                getApplicationEventProperties(applicationId)
              );
            }

            onClose();
          }}
        />
      )}
      {state.matches({ storesBlock: { storeEdit: 'storeAddress' } }) && (
        <StoreAddress
          storeDraft={storeDraft}
          onSubmit={async (data) => {
            ampli.mpPaOfflineStoreAddressScreenNextClick(
              getApplicationEventProperties(applicationId)
            );
            actor.send({ type: 'storeAddress.submitData', storeAddress: data });
            actor.send({ type: 'next' });
          }}
          onBack={() => {
            ampli.mpPaOfflineStoreAddressScreenBackClick(
              getApplicationEventProperties(applicationId)
            );
            onBack();
          }}
          onClose={() => {
            ampli.mpPaOfflineStoreAddressScreenCloseClick(
              getApplicationEventProperties(applicationId)
            );
            onClose();
          }}
        />
      )}
      {state.matches({ storesBlock: { storeEdit: 'storeMap' } }) && (
        <StoreMap
          storeDraft={storeDraft}
          onSubmit={(data) => {
            if (data.coordinates === null) {
              ampli.mpPaOfflineStoreMapScreenCannotFindClick(
                getApplicationEventProperties(applicationId)
              );
            } else {
              ampli.mpPaOfflineStoreMapScreenConfirmClick(
                getApplicationEventProperties(applicationId)
              );
            }

            actor.send({
              type: 'storeMap.submitData',
              storeCoordinates: data.coordinates,
            });
          }}
          onBack={() => {
            ampli.mpPaOfflineStoreMapScreenBackClick(
              getApplicationEventProperties(applicationId)
            );
            onBack();
          }}
          onClose={() => {
            ampli.mpPaOfflineStoreMapScreenCloseClick(
              getApplicationEventProperties(applicationId)
            );
            onClose();
          }}
        />
      )}
      {(state.matches({ storesBlock: { storeEdit: 'storePhotos' } }) ||
        isOfflineDraftReady) && (
        <StorePhotos
          applicationId={applicationId}
          storeDraft={storeDraft}
          onSubmit={async (data) => {
            ampli.mpPaOfflineStorePhotosScreenNextClick(
              getApplicationEventProperties(applicationId)
            );
            actor.send({ type: 'storePhotos.submitData', storePhotos: data });
            await updateApplication({
              onError: () => {
                actor.send({ type: 'storeDraftReady.invalidated' });
              },
            });
            actor.send({ type: 'next' });
          }}
          onBack={() => {
            ampli.mpPaOfflineStorePhotosScreenBackClick(
              getApplicationEventProperties(applicationId)
            );
            onBack();
          }}
          onClose={() => {
            ampli.mpPaOfflineStorePhotosScreenCloseClick(
              getApplicationEventProperties(applicationId)
            );
            onClose();
          }}
        />
      )}
    </AddressSuggestionApiProvider>
  );
};
